import React from "react";
import { useTable, useSortBy } from "react-table";
import { classNames } from "../components/shared/Utils";
import {
    SortIcon,
    SortUpIcon,
    SortDownIcon,
} from "../../src/components/shared/Icons";

// StatusPill component for status display
export function StatusPill({ value }) {
    const status = value ? value.toLowerCase() : "unknown";

    return (
        <span
            className={classNames(
                "px-3 py-1 uppercase leading-wide font-bold text-xs rounded-full shadow-sm",
                status.startsWith("active") ? "bg-green-100 text-green-800" : null,
                status.startsWith("inactive")
                    ? "bg-yellow-100 text-yellow-800"
                    : null,
                status.startsWith("offline") ? "bg-red-100 text-red-800" : null
            )}
        >
            {status}
        </span>
    );
}

// AvatarCell component for displaying user info with avatar
export function AvatarCell({ value, column, row }) {
    return (
        <div className="flex items-center">
            <div className="flex-shrink-0 w-10 h-10">
                <img
                    className="w-10 h-10 rounded-full"
                    src={row.original[column.imgAccessor]}
                    alt=""
                />
            </div>
            <div className="ml-4">
                <div className="text-sm font-medium text-gray-900">{value}</div>
                <div className="text-sm text-gray-500">
                    {row.original[column.emailAccessor]}
                </div>
            </div>
        </div>
    );
}

function BasicTable({ columns, data }) {
    const visibleColumnCount = columns.length || 1;

    // Use the state and functions returned from useTable to build your UI
    const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } =
        useTable(
            {
                columns,
                data,
            },
            useSortBy
        );

    // Render the UI for your table
    return (
        <div className="flex flex-col">
            <div className="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
                <div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
                    <div className="overflow-hidden border-b border-gray-200 shadow sm:rounded-lg">
                        <table
                            {...getTableProps()}
                            className="min-w-full divide-y divide-gray-200"
                        >
                            <thead className="bg-gray-50">
                                {headerGroups.map((headerGroup) => (
                                    <tr {...headerGroup.getHeaderGroupProps()}>
                                        {headerGroup.headers.map((column) => (
                                            <th
                                                scope="col"
                                                className="px-6 py-3 text-xs font-medium tracking-normal text-left text-gray-500 uppercase group"
                                                {...column.getHeaderProps(
                                                    column.getSortByToggleProps()
                                                )}
                                            >
                                                <div className="flex items-center justify-between">
                                                    {column.render("Header")}
                                                    {/* Add a sort direction indicator */}
                                                    <span>
                                                        {column.isSorted ? (
                                                            column.isSortedDesc ? (
                                                                <SortDownIcon className="w-4 h-4 text-gray-400" />
                                                            ) : (
                                                                <SortUpIcon className="w-4 h-4 text-gray-400" />
                                                            )
                                                        ) : (
                                                            <SortIcon className="w-4 h-4 text-gray-400 opacity-0 group-hover:opacity-100" />
                                                        )}
                                                    </span>
                                                </div>
                                            </th>
                                        ))}
                                    </tr>
                                ))}
                            </thead>
                            <tbody
                                {...getTableBodyProps()}
                                className="bg-white divide-y divide-gray-200"
                            >
                                {rows.length > 0 ? (
                                    rows.map((row) => {
                                        prepareRow(row);
                                        return (
                                            <tr {...row.getRowProps()}>
                                                {row.cells.map((cell) => {
                                                    return (
                                                        <td
                                                            {...cell.getCellProps()}
                                                            className="px-6 py-4 overflow-hidden text-ellipsis"
                                                            role="cell"
                                                        >
                                                            {cell.column.Cell.name ===
                                                            "defaultRenderer" ? (
                                                                <div className="text-sm text-gray-500">
                                                                    {cell.render(
                                                                        "Cell"
                                                                    )}
                                                                </div>
                                                            ) : (
                                                                cell.render("Cell")
                                                            )}
                                                        </td>
                                                    );
                                                })}
                                            </tr>
                                        );
                                    })
                                ) : (
                                    <tr>
                                        <td
                                            colSpan={visibleColumnCount}
                                            className="px-6 py-10 text-sm text-center text-gray-500"
                                        >
                                            No data available
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default BasicTable;
